import { VALIDATION_LIMITS } from './validation-limits';

export const ValidationMessages = {
  PASSWORD_REQUIRED: 'Password is required',
  PASSWORD_TOO_SHORT: 'Password must be at least 12 characters long',
  PASSWORD_MISSING_NUMBER: 'Password must contain at least one number',
  PASSWORD_MISSING_SPECIAL_SYMBOL: 'Password must contain at least one special character',
  PASSWORD_MISSING_LETTER: 'Password must contain at least one letter',
  PASSWORD_DO_NOT_MATCH: 'Passwords do not match',
  PASSWORD_SAME_AS_CURRENT: 'New password must be different from current password.',
  CURRENT_PASSWORD_INCORRECT: 'Current password is incorrect',
  PASSWORD_CHANGE_RATE_LIMIT: 'You changed your password less than an hour ago. Try again later.',

  EMAIL_REQUIRED: 'Email is required',
  PHONE_REQUIRED: 'Phone is required',
  EMAIL_INVALID_FORMAT: 'Invalid email format',
  EMAIL_INVALID_ADDRESS: 'Invalid email address',

  FIRST_NAME_REQUIRED: 'First name is required',
  FIRST_NAME_TOO_SHORT: `First name must be at least ${VALIDATION_LIMITS.FIRST_NAME_MIN} characters`,
  FIRST_NAME_TOO_LONG: `First name must not exceed ${VALIDATION_LIMITS.FIRST_NAME_MAX} characters`,
  LAST_NAME_REQUIRED: 'Last name is required',
  LAST_NAME_TOO_SHORT: `Last name must be at least ${VALIDATION_LIMITS.LAST_NAME_MIN} characters`,
  LAST_NAME_TOO_LONG: `Last name must not exceed ${VALIDATION_LIMITS.LAST_NAME_MAX} characters`,
  CONTACT_MESSAGE_REQUIRED: 'Message is required',
  CONTACT_MESSAGE_TOO_LONG: `Message must not exceed ${VALIDATION_LIMITS.CONTACT_MESSAGE_MAX} characters`,
  CONTACT_TOPIC_REQUIRED: 'Please select what we can help you with',
  LINKEDIN_PROFILE_REQUIRED: 'Linkedin profile is required',
  LINKEDIN_REQUIRED: 'LinkedIn is required',
  LINKEDIN_INVALID_URL: 'linkedin must be a URL address',
  ABOUT_ME_REQUIRED: 'About me is required minimum 20 words',
  ABOUT_ME_TOO_SHORT: `Please provide at least ${VALIDATION_LIMITS.ABOUT_ME_MIN} characters`,
  ABOUT_ME_TOO_LONG: `Description must not exceed ${VALIDATION_LIMITS.ABOUT_ME_MAX} characters`,
  POSITION_REQUIRED: 'Position is required',
  JOB_TITLE_REQUIRED: 'Job title is required',
  JOB_TITLE_TOO_LONG: `Job title must not exceed ${VALIDATION_LIMITS.JOB_TITLE_MAX} characters`,
  COMPANY_REQUIRED: 'Company is required',
  COMPANY_NAME_TOO_LONG: `Company name must not exceed ${VALIDATION_LIMITS.COMPANY_NAME_MAX} characters`,
  TAGLINE_TOO_LONG: `Tagline must not exceed ${VALIDATION_LIMITS.TAGLINE_MAX} characters`,
  COUNTRY_REQUIRED: 'Country is required',
  CURRENT_STAGE_REQUIRED: 'Current Stage is required',
  INDUSTRIES_REQUIRED: 'Select your industries is required',
  TIME_DEDICATION_REQUIRED: 'Time dedication is required',
  INTERESTS_REQUIRED: 'Select your interests is required',
  PRIMARY_CATEGORY_REQUIRED: 'Primary category is required',
  MOTIVATION_REQUIRED: 'Tell us about your motivation is required',
  MOTIVATION_TOO_SHORT: `Please provide at least ${VALIDATION_LIMITS.MOTIVATION_MIN} characters`,
  MOTIVATION_TOO_LONG: `Description must not exceed ${VALIDATION_LIMITS.MOTIVATION_MAX} characters`,
  X_INVALID_URL: 'x must be a URL address',
  INSTAGRAM_INVALID_URL: 'instagram must be a URL address',
  TIKTOK_INVALID_URL: 'tiktok must be a URL address',
  APPLY_FIELDS_REQUIRED: 'Select your mentoring areas is required',
  EXPERTISE_REQUIRED: 'Please select at least one expertise area',
  SESSION_PRICING_REQUIRED: 'At least one session duration must be enabled with a price',
  PARTNER_NAME_REQUIRED: 'Partner name is required',
  PARTNER_NAME_TOO_SHORT: 'Partner name must be at least 2 characters',
  PARTNER_EMAIL_DOMAIN_REQUIRED: 'Email domain is required',
  PARTNER_INDUSTRY_REQUIRED: 'Industry is required',
  PARTNER_SIZE_REQUIRED: 'Partner size is required',
  PARTNER_MAX_SEATS_REQUIRED: 'Max seats is required',

  TITLE_REQUIRED: 'Title is required',
  DESCRIPTION_REQUIRED: 'Description is required',

  INVALID_URL: 'Invalid URL',
  VIDEO_URL_REQUIRED: 'Video URL is required',
  VIDEO_URL_INVALID: 'Enter a valid http(s) or Vimeo video URL',

  NAME_REQUIRED: 'Name is required',
  PHOTO_LINK_REQUIRED: 'Photo Link is required',

  CONTENT_REQUIRED: 'Content is required',
  SUBJECT_REQUIRED: 'Subject is required',
  BODY_REQUIRED: 'Body is required',
  MENTOR_REQUIRED: 'Mentor is required',
  START_DATE_TIME_REQUIRED: 'Start Date and Time is required',
  TIME_REQUIRED: 'Time is required',
  TIMEZONE_REQUIRED: 'Timezone is required',
  DURATION_REQUIRED: 'Duration is required',
  QUESTION_REQUIRED: 'Question is required',
  ANSWER_REQUIRED: 'Answer is required',
  ASKED_BY_REQUIRED: 'Who asked this question is required',
  HOW_TO_USE_REQUIRED: 'How to use is required',
  TERMS_OF_SERVICE_REQUIRED: 'Terms of Service is required',
  VALUE_REQUIRED: 'Value is required',
  FILE_REQUIRED: 'At least one file is required',
  CATEGORY_REQUIRED: 'Category is required',
  REQUIRED_FIELD: 'This field is required',
} as const;

export type ValidationMessage = (typeof ValidationMessages)[keyof typeof ValidationMessages];

/**
 * Messages returned in API responses today (HTTP/GraphQL `message`).
 * For end-user localization, prefer a follow-up migration to stable error
 * codes in the payload and map them to i18n keys in the client — not
 * `tStatic` or server-side translation.
 */
export const AuthErrorMessages = {
  INVALID_CREDENTIALS: 'Invalid credentials',
  ACCOUNT_INACTIVE: 'User account is inactive',
  ACCESS_DENIED: 'Access denied',
  /** Shown at sign-in, and only after the correct password, so the form never reveals which emails exist. */
  SIGN_IN_ACCOUNT_SUSPENDED: 'Account suspended, contact support',
  SIGN_IN_ACCOUNT_INACTIVE: 'Account inactive, contact support',
  ACCOUNT_LOCKED: 'Account is locked',
  ACCOUNT_LOCKED_DUE_TO_FAILED_ATTEMPTS: 'Account is locked due to too many failed login attempts',
  INVALID_RESET_TOKEN: 'Invalid, expired, or already used reset token',
  PASSWORD_USED_RECENTLY: 'Password has been used recently. Please choose a different password.',
  PASSWORD_SAVED_CLEANUP_PENDING:
    'Your password was saved. Session cleanup is pending. Sign in with your new password; do not reuse this reset link.',
} as const;

const MS_PER_MINUTE = 60_000;

/** Sign-in message for a locked account that names the remaining time (at least one minute). */
export function formatSignInAccountLockedMessage(remainingMs: number): string {
  const minutes = Math.max(1, Math.ceil(remainingMs / MS_PER_MINUTE));
  return `Account locked, try again in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
}

export type AuthErrorMessage = (typeof AuthErrorMessages)[keyof typeof AuthErrorMessages];

/**
 * Reset-password toast allowlist: show these API messages; fall back for all others.
 */
export const RESET_PASSWORD_USER_FACING_ERROR_MESSAGES = [
  AuthErrorMessages.INVALID_RESET_TOKEN,
  AuthErrorMessages.PASSWORD_USED_RECENTLY,
  AuthErrorMessages.PASSWORD_SAVED_CLEANUP_PENDING,
] as const;
